import {
  ChangeEvent,
  DragEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import LinkIcon from "@mui/icons-material/Link";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  MAX_DOCUMENT_SIZE_BYTES,
  getWorkspaces,
  uploadFile,
  uploadUrl,
} from "@services/api";
import { createReading } from "@features/workspace-readings/state/workspaceReadingsSlice";

const NO_WORKSPACE = "__none__";

interface PickerWorkspace {
  workspaceId: string;
  workspaceName: string;
}

// Soft limit triggers a warning chip; hard limit rejects the file at
// queue time. Most academic PDFs sit well under 50 MB; the 100 MB
// ceiling covers the occasional course-pack without inviting truly
// huge uploads (S3 PUT would succeed but the user's bandwidth and
// browser memory wouldn't enjoy it).
const SOFT_LIMIT_BYTES = 50 * 1024 * 1024;
const HARD_LIMIT_BYTES = MAX_DOCUMENT_SIZE_BYTES;
// Limit concurrent uploads so a 20-file batch doesn't saturate the
// user's upstream and trigger S3-side rate limiting.
const POOL_SIZE = 3;

type RowSource =
  | { kind: "file"; file: File }
  | { kind: "url"; url: string };

type RowStatus = "queued" | "uploading" | "linking" | "done" | "failed";

interface QueueRow {
  id: string;
  name: string;
  source: RowSource;
  status: RowStatus;
  progress: number;
  error?: string;
  // Set when upload succeeds, even if a later reading-creation step fails.
  documentId?: string;
  // Soft warning surfaced on the row chip (e.g. "uploaded, reading
  // creation failed"). Doesn't move the row to "failed" because the
  // file is in the library.
  warning?: string;
  // Honored mid-flight by axios. Replaced on retry.
  abortController?: AbortController;
}

interface Props {
  modalVisible: string;
  setModalVisible: Dispatch<SetStateAction<string>>;
  // Fired after the first successful upload (and on every subsequent
  // one). Parent uses it to refetch the /files table/grid.
  onUploaded?: () => void;
}

const newId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `q-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const stripExt = (name: string) => name.replace(/\.pdf$/i, "");
const fileSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;

const PdfSourceModal = ({
  modalVisible,
  setModalVisible,
  onUploaded,
}: Props) => {
  const dispatch = useAppDispatch();
  const userState = useAppSelector((state) => state.user);
  const currentWorkspaceId = useAppSelector(
    (state) => state.workspace.workspace_id
  );

  const [rows, setRows] = useState<QueueRow[]>([]);
  const [urlText, setUrlText] = useState("");
  // Workspaces the user owns. Fetched on modal open so the user can
  // route a batch to any owned workspace, not just whichever one is
  // currently active in the global state.
  const [ownedWorkspaces, setOwnedWorkspaces] = useState<PickerWorkspace[]>(
    []
  );
  // The currently picked target. NO_WORKSPACE means "don't link" — the
  // documents end up in the user's library and nothing more. Defaults
  // to the global current workspace iff the user actually owns it,
  // otherwise NO_WORKSPACE (so we never silently auto-target something
  // the user doesn't control).
  const [selectedWorkspaceId, setSelectedWorkspaceId] =
    useState<string>(NO_WORKSPACE);
  const [error, setError] = useState<string>("");
  const [dragOver, setDragOver] = useState(false);

  // Refs used by the pool runner: rowsRef so the runner sees the latest
  // queue without re-creating the function; slotsRef to track which row
  // ids are in-flight so we don't double-process.
  const rowsRef = useRef<QueueRow[]>([]);
  rowsRef.current = rows;
  const slotsRef = useRef<Set<string>>(new Set());
  const targetWorkspaceIdRef = useRef<string>(selectedWorkspaceId);
  targetWorkspaceIdRef.current = selectedWorkspaceId;

  const inFlightCount = rows.filter(
    (r) => r.status === "uploading" || r.status === "linking"
  ).length;
  const doneCount = rows.filter((r) => r.status === "done").length;
  const failedCount = rows.filter((r) => r.status === "failed").length;
  const isOpen = modalVisible === "choosePdf";

  // ------- row mutation helpers --------------------------------------

  const setRowStatus = (id: string, patch: Partial<QueueRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
  };

  const removeRow = (id: string) => {
    const row = rowsRef.current.find((r) => r.id === id);
    if (row?.abortController) {
      row.abortController.abort();
    }
    slotsRef.current.delete(id);
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  // ------- pool runner ----------------------------------------------

  const processRow = useCallback(
    async (row: QueueRow): Promise<void> => {
      const controller = new AbortController();
      setRowStatus(row.id, {
        status: "uploading",
        progress: 0,
        error: undefined,
        warning: undefined,
        abortController: controller,
      });

      let documentId: string | null = null;
      let reused = false;
      let reusedAs: string | undefined;

      if (row.source.kind === "file") {
        const result = await uploadFile(
          row.name,
          row.source.file,
          (pct) => setRowStatus(row.id, { progress: pct }),
          controller.signal
        );
        if (controller.signal.aborted) {
          setRowStatus(row.id, {
            status: "failed",
            error: "Cancelled",
            abortController: undefined,
          });
          return;
        }
        if (!result.success) {
          setRowStatus(row.id, {
            status: "failed",
            error: result.error.message,
            abortController: undefined,
          });
          return;
        }
        documentId = result.data.documentId;
        reused = result.data.reused;
        reusedAs = result.data.existingName;
      } else {
        const result = await uploadUrl(
          row.name,
          row.source.url,
          controller.signal
        );
        if (controller.signal.aborted) {
          setRowStatus(row.id, {
            status: "failed",
            error: "Cancelled",
            abortController: undefined,
          });
          return;
        }
        if (!result.success) {
          setRowStatus(row.id, {
            status: "failed",
            error: result.error.message,
            abortController: undefined,
          });
          return;
        }
        documentId = result.data.documentId;
        reused = result.data.reused;
        reusedAs = result.data.existingName;
        setRowStatus(row.id, { progress: 100 });
      }

      const reusedWarning = reused
        ? reusedAs && reusedAs !== row.name
          ? `Already in your library as "${reusedAs}" — reused existing copy.`
          : "Already in your library — reused existing copy."
        : undefined;

      // Optional reading-creation step. A failure here doesn't fail the
      // whole row — the document is in the library and the user can
      // link it from /files later.
      const targetWsId = targetWorkspaceIdRef.current;
      if (targetWsId !== NO_WORKSPACE && documentId) {
        setRowStatus(row.id, { status: "linking", documentId });
        try {
          await dispatch(
            createReading({
              workspace_id: targetWsId,
              document_id: documentId,
              reading_name: row.name,
              required: false,
              assign_to_all: true,
            })
          ).unwrap();
        } catch (readingErr) {
          console.warn("Reading creation failed for", row.name, readingErr);
          // Combine the link warning with the reused warning when both
          // apply so the row carries a single, clear chip.
          const combined = reusedWarning
            ? `${reusedWarning} Linking to workspace failed.`
            : "Uploaded, but linking to workspace failed.";
          setRowStatus(row.id, {
            status: "done",
            documentId,
            warning: combined,
            abortController: undefined,
          });
          onUploaded?.();
          return;
        }
      }

      setRowStatus(row.id, {
        status: "done",
        documentId: documentId ?? undefined,
        warning: reusedWarning,
        abortController: undefined,
      });
      onUploaded?.();
    },
    [dispatch, onUploaded]
  );

  // Tick the pool — start up to POOL_SIZE queued rows. Called on every
  // queue change. Async chains call tick again as slots open so the
  // pool stays drained.
  const tick = useCallback(() => {
    while (slotsRef.current.size < POOL_SIZE) {
      const next = rowsRef.current.find(
        (r) => r.status === "queued" && !slotsRef.current.has(r.id)
      );
      if (!next) break;
      slotsRef.current.add(next.id);
      void processRow(next).finally(() => {
        slotsRef.current.delete(next.id);
        tick();
      });
    }
  }, [processRow]);

  useEffect(() => {
    if (isOpen) tick();
  }, [rows, isOpen, tick]);

  // Hydrate the workspace picker each time the modal opens. Cheap call
  // (single GET, no pagination) and keeps the list fresh for users who
  // just created a workspace before opening Upload.
  useEffect(() => {
    if (!isOpen || !userState.userId) return;
    let cancelled = false;
    void (async () => {
      const result = await getWorkspaces();
      if (cancelled) return;
      if (!result.success) {
        // Silent failure — picker just stays at "Don't link". The
        // upload path still works.
        console.warn("PdfSourceModal: failed to load workspaces", result.error);
        return;
      }
      const list = Array.isArray(result.data) ? result.data : [];
      const owned = list
        .filter((ws) =>
          (ws.workspace_permissions ?? []).some(
            (p) =>
              p.user_id === userState.userId && p.permission_name === "owner"
          )
        )
        .map((ws) => ({
          workspaceId: ws.workspace_id,
          workspaceName: ws.workspace_name,
        }));
      setOwnedWorkspaces(owned);
      // Default to the current workspace iff the user owns it. Otherwise
      // keep NO_WORKSPACE so we never silently target something the
      // user doesn't control.
      const currentIsOwned = owned.some(
        (w) => w.workspaceId === currentWorkspaceId
      );
      setSelectedWorkspaceId(
        currentIsOwned ? currentWorkspaceId : NO_WORKSPACE
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, userState.userId, currentWorkspaceId]);

  // ------- enqueue helpers ------------------------------------------

  const enqueueFiles = (files: File[]) => {
    setError("");
    const accepted: QueueRow[] = [];
    const rejected: string[] = [];
    for (const file of files) {
      if (file.size > HARD_LIMIT_BYTES) {
        rejected.push(
          `${file.name} (${fileSize(file.size)}) exceeds the 100 MB limit.`
        );
        continue;
      }
      const isOverSoft = file.size > SOFT_LIMIT_BYTES;
      accepted.push({
        id: newId(),
        name: stripExt(file.name).slice(0, 200),
        source: { kind: "file", file },
        status: "queued",
        progress: 0,
        warning: isOverSoft
          ? `Large file (${fileSize(file.size)}) — upload may be slow.`
          : undefined,
      });
    }
    if (rejected.length > 0) {
      setError(rejected.join(" "));
    }
    if (accepted.length > 0) {
      setRows((prev) => [...prev, ...accepted]);
    }
  };

  const enqueueUrls = () => {
    const urls = urlText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (urls.length === 0) return;
    setError("");
    const accepted: QueueRow[] = [];
    const rejected: string[] = [];
    for (const url of urls) {
      try {
        // Validate the URL shape; let the server decide whether the
        // content is actually a PDF — we don't want to be wrong about
        // edge-case URL formats.
        new URL(url);
      } catch {
        rejected.push(url);
        continue;
      }
      accepted.push({
        id: newId(),
        name: url.split("/").pop()?.replace(/\.pdf$/i, "") || url,
        source: { kind: "url", url },
        status: "queued",
        progress: 0,
      });
    }
    if (rejected.length > 0) {
      setError(`Invalid URLs: ${rejected.join(", ")}`);
    }
    if (accepted.length > 0) {
      setRows((prev) => [...prev, ...accepted]);
      setUrlText("");
    }
  };

  // ------- input handlers -------------------------------------------

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    enqueueFiles(files);
    // Reset the input so the same file can be added again after removal.
    e.target.value = "";
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files ?? []).filter((f) =>
      f.type === "application/pdf" || /\.pdf$/i.test(f.name)
    );
    enqueueFiles(dropped);
  };

  const handleNameEdit = (id: string, name: string) => {
    setRowStatus(id, { name });
  };

  const handleRetry = (id: string) => {
    setRowStatus(id, {
      status: "queued",
      error: undefined,
      progress: 0,
      abortController: undefined,
    });
  };

  // ------- close behavior -------------------------------------------

  const reset = () => {
    setRows([]);
    setUrlText("");
    setError("");
    slotsRef.current.clear();
  };

  const handleClose = () => {
    if (inFlightCount > 0) {
      if (
        !window.confirm(
          `${inFlightCount} upload${
            inFlightCount === 1 ? " is" : "s are"
          } still in progress. Cancel and close?`
        )
      ) {
        return;
      }
      // Abort in-flight requests so they don't keep mutating state
      // after unmount.
      rowsRef.current.forEach((r) => r.abortController?.abort());
    }
    setModalVisible("");
    reset();
  };

  if (!isOpen) return null;
  if (!userState.userId) {
    // Defensive: the parent only opens this when authed, but if the
    // user state drops mid-modal we want to surface that instead of
    // silently failing on submit.
    return (
      <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Upload files</DialogTitle>
        <DialogContent>
          <Alert severity="warning">You need to be signed in to upload.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Upload files
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={handleClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: 2,
            borderStyle: "dashed",
            borderColor: dragOver ? "primary.main" : "divider",
            borderRadius: 1,
            p: 3,
            textAlign: "center",
            bgcolor: dragOver ? "action.hover" : "transparent",
            transition: "all 0.15s",
            mb: 2,
          }}
        >
          <UploadFileOutlinedIcon
            sx={{ fontSize: 32, color: "text.secondary", mb: 1 }}
          />
          <Typography variant="body2" sx={{ mb: 1 }}>
            Drop PDFs here or pick from your computer
          </Typography>
          <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={<UploadFileOutlinedIcon />}
          >
            Choose files
            <input
              hidden
              multiple
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleFileInput}
            />
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Up to 100 MB each. Files over 50 MB will be flagged as large.
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Or paste URLs, one per line:
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              size="small"
              value={urlText}
              placeholder="https://arxiv.org/pdf/2401.00001.pdf"
              onChange={(e) => setUrlText(e.target.value)}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<LinkIcon />}
              onClick={enqueueUrls}
              disabled={!urlText.trim()}
            >
              Add
            </Button>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            URL imports stay live — if the file moves, the link breaks.
          </Typography>
        </Box>

        {rows.length > 0 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                Queue ({doneCount} of {rows.length} complete
                {failedCount > 0 ? `, ${failedCount} failed` : ""})
              </Typography>
            </Box>
            <Stack spacing={1}>
              {rows.map((row) => (
                <QueueRowView
                  key={row.id}
                  row={row}
                  onNameChange={(name) => handleNameEdit(row.id, name)}
                  onRemove={() => removeRow(row.id)}
                  onRetry={() => handleRetry(row.id)}
                />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "space-between",
          px: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 280, flex: 1 }}>
          <InputLabel id="upload-workspace-label">
            Add to workspace as readings
          </InputLabel>
          <Select
            labelId="upload-workspace-label"
            label="Add to workspace as readings"
            value={selectedWorkspaceId}
            onChange={(e: SelectChangeEvent) =>
              setSelectedWorkspaceId(e.target.value)
            }
          >
            <MenuItem value={NO_WORKSPACE}>
              <em>Don't link — upload to library only</em>
            </MenuItem>
            {ownedWorkspaces.map((w) => (
              <MenuItem key={w.workspaceId} value={w.workspaceId}>
                {w.workspaceName}
              </MenuItem>
            ))}
            {ownedWorkspaces.length === 0 && (
              <MenuItem value="__empty__" disabled>
                <em>You don't own any workspaces yet.</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>
        <Button onClick={handleClose} variant="contained">
          {inFlightCount > 0 ? "Cancel" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------

interface RowViewProps {
  row: QueueRow;
  onNameChange: (name: string) => void;
  onRemove: () => void;
  onRetry: () => void;
}

const QueueRowView = ({
  row,
  onNameChange,
  onRemove,
  onRetry,
}: RowViewProps) => {
  const sizeLabel =
    row.source.kind === "file" ? fileSize(row.source.file.size) : "URL";
  const isFile = row.source.kind === "file";
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto auto",
        alignItems: "center",
        gap: 1,
        p: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      {isFile ? (
        <PictureAsPdfOutlinedIcon
          fontSize="small"
          sx={{ color: "text.secondary" }}
        />
      ) : (
        <LinkIcon fontSize="small" sx={{ color: "text.secondary" }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <TextField
          variant="standard"
          size="small"
          value={row.name}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={row.status === "uploading" || row.status === "linking"}
          fullWidth
          inputProps={{ maxLength: 255 }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 0.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {sizeLabel}
          </Typography>
          <StatusChip row={row} />
          {row.warning && (
            <Tooltip title={row.warning}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="warning"
              />
            </Tooltip>
          )}
        </Box>
        {row.status === "uploading" && (
          <LinearProgress
            variant="determinate"
            value={row.progress}
            sx={{ mt: 0.5 }}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {row.status === "failed" && (
          <Tooltip title={row.error || "Retry"}>
            <IconButton size="small" onClick={onRetry}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip
          title={
            row.status === "uploading" || row.status === "linking"
              ? "Cancel"
              : "Remove"
          }
        >
          <IconButton size="small" onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const StatusChip = ({ row }: { row: QueueRow }) => {
  switch (row.status) {
    case "queued":
      return <Chip size="small" variant="outlined" label="queued" />;
    case "uploading":
      return (
        <Chip
          size="small"
          variant="outlined"
          icon={<CircularProgress size={12} />}
          label={`${row.progress}%`}
        />
      );
    case "linking":
      return (
        <Chip
          size="small"
          variant="outlined"
          icon={<CircularProgress size={12} />}
          label="linking"
        />
      );
    case "done":
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<CheckCircleOutlineIcon fontSize="small" />}
          label="done"
        />
      );
    case "failed":
      return (
        <Tooltip title={row.error || "Failed"}>
          <Chip
            size="small"
            color="error"
            variant="outlined"
            icon={<ErrorOutlineIcon fontSize="small" />}
            label="failed"
          />
        </Tooltip>
      );
  }
};

export default PdfSourceModal;
